import { registerOp, resolveOp, enqueue } from './outbox';
import { setPending } from './pending';
import { isOnline, marcarInalcancavel } from './status';
import { currentUserId } from '../auth';
import { isNetworkError, isAuthError, SESSION_EXPIRED_MESSAGE } from '../errors';

/**
 * Ponte entre a camada de dados e a fila.
 *
 * Uma escrita registrada aqui tenta ir para o servidor; se não houver conexão,
 * entra na fila e sobe depois. O chamador não precisa saber qual dos dois
 * aconteceu — em ambos os casos o lançamento está guardado.
 *
 * O que NÃO passa por aqui, de propósito: Edge Functions (criação de usuário,
 * licença) e operações de administração de laboratório. As
 * primeiras só existem online; as segundas precisam de confirmação imediata do
 * servidor e não fazem sentido guardadas para depois. Offline elas falham com
 * a mensagem de sempre, o que é a resposta honesta.
 */

/** Devolvido quando a escrita foi enfileirada em vez de enviada. */
export const QUEUED = Object.freeze({ queued: true });

export const wasQueued = (result) => result === QUEUED;

/** Nunca deixa a identidade derrubar o enfileiramento. */
const idDoUsuario = async () => {
  try {
    return await currentUserId();
  } catch {
    return null;
  }
};

/**
 * Declara uma escrita enfileirável e devolve a função a ser usada pelo app.
 *
 * `name` vai para o disco: mudar sem migração faz um registro gravado ontem
 * não achar a função hoje.
 *
 * `pendingOf` serve para as sessões de workflow, que entram e saem no mesmo
 * formato. `patchOf` serve para as listas (clientes, serviços, animais), onde
 * o que precisa mudar é uma linha dentro de um resultado já em cache.
 */
export function queueable(name, fn, { pendingOf, patchOf } = {}) {
  registerOp(name, fn);

  return async (...args) => {
    const enqueueIt = async () => {
      const pending = pendingOf ? pendingOf(...args) : null;
      if (pending) await setPending(pending.kind, pending.id, pending.data);
      await enqueue(name, args, {
        userId: await idDoUsuario(),
        pendingKey: pending ? `pendente:${pending.kind}:${pending.id}` : null,
        label: pending?.label || name,
        // Emenda que a leitura aplica por cima enquanto o item não sobe. Sem
        // ela, o lançamento entra na fila mas some da tela até sincronizar.
        patch: patchOf ? patchOf(...args) : null,
      });
      return QUEUED;
    };

    if (!isOnline()) return enqueueIt();

    try {
      return await resolveOp(name)(...args);
    } catch (err) {
      if (isNetworkError(err)) {
        // A escrita que falhou é a evidência mais direta de que o servidor não
        // está acessível — vale mais que a última verificação periódica.
        marcarInalcancavel();
        return enqueueIt();
      }

      // Sessão vencida não vira "pendente": o lançamento NÃO foi guardado, e
      // dizer que foi seria mentir para quem está lançando. Vira uma mensagem
      // que diz o que fazer, em vez do erro cru do servidor.
      if (isAuthError(err)) {
        const e = new Error(SESSION_EXPIRED_MESSAGE);
        e.cause = err;
        e.sessionExpired = true;
        throw e;
      }

      throw err;
    }
  };
}
